"use client";

import {
  type ReactNode,
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
  useSyncExternalStore,
} from "react";
import SideNavigation, {
  type SideNavigationProps,
} from "@cloudscape-design/components/side-navigation";
import AppLayout, {
  type AppLayoutProps,
} from "@cloudscape-design/components/app-layout";
import SplitPanel from "@cloudscape-design/components/split-panel";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import BreadcrumbGroup from "@cloudscape-design/components/breadcrumb-group";
import TopNavigation, {
  type TopNavigationProps,
} from "@cloudscape-design/components/top-navigation";
import SpaceBetween from "@cloudscape-design/components/space-between";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/features/auth/useAuth";
import { useClientView } from "@/features/auth/useClientView";
import { useClientViewRouteGuard } from "@/features/auth/useClientViewRouteGuard";
import { useBreadcrumbOverride } from "@/components/ui/BreadcrumbContext";
import { useHelpPanel } from "@/features/help/HelpPanelContext";
import { HelpPanelWidget } from "@/features/help/HelpPanelContent";
import { useDashboardContents } from "@/features/london-dashboard/DashboardContentsContext";
import { useInspect } from "@/features/inspect/InspectContext";
import { InspectDrawerContent } from "@/features/inspect/InspectDrawerContent";
import { useExplainPanel } from "@/features/report/explain/ExplainPanelContext";
import { ExplainPanel } from "@/features/report/explain/ExplainPanel";
import {
  setViewAsTenant,
  getViewAsTenantId,
} from "@/features/admin/TenantSwitcher";
import { ViewingAsBanner } from "@/features/admin/ViewingAsBanner";
import { Footer } from "@/components/ui/Footer";
import { PRODUCT_NAME } from "@/lib/brand";
import { applyDensity, Density } from "@cloudscape-design/global-styles";

interface AppShellProps {
  children: ReactNode;
}

// ---------------------------------------------------------------------------
// Hash-aware active state (mission report-nav-and-narrative-01KXE9QP, WP02/T004, R2)
// ---------------------------------------------------------------------------

/**
 * `window.location.hash` as an EXTERNAL store (`useSyncExternalStore`, not a `useState` +
 * `useEffect` pair): Next's own `usePathname()` never reflects the URL's hash fragment, and a
 * hash-only change (`navigateToHashOnCurrentPage`, or any other same-page anchor click) fires a
 * native `hashchange` event without changing `pathname` at all — subscribing to that event is
 * the correct react-hooks pattern for synchronising with this kind of external, imperatively-
 * mutated browser state. It also covers the OTHER case for free: a cross-page navigation to a
 * URL that already carries its own hash won't fire `hashchange` (`History.pushState` doesn't),
 * but `pathname` DOES change then, forcing a re-render — and `useSyncExternalStore` re-reads its
 * snapshot on every render, so the fresh hash is picked up without a second, effect-based
 * re-sync (which would call `setState` synchronously inside an effect body, an anti-pattern the
 * `react-hooks/set-state-in-effect` lint rule specifically flags).
 */
function subscribeToHashChange(onStoreChange: () => void): () => void {
  window.addEventListener("hashchange", onStoreChange);
  return () => window.removeEventListener("hashchange", onStoreChange);
}

function getHashSnapshot(): string {
  return window.location.hash;
}

function getServerHashSnapshot(): string {
  return "";
}

// ---------------------------------------------------------------------------
// Sidebar navigation
// ---------------------------------------------------------------------------

// Product-areas IA (mission report-ux-professionalisation, WP02/T004): the
// sidebar's product-area section renders PURELY from the `PRODUCT_AREAS`
// registry (features/navigation/productAreas.ts) — no hardcoded flow-IA
// groups. This replaces the old Summary/Arisings/Borough Performance/
// Capacity/Apportionment/Targets sections (their routes moved wholesale to
// /archive/* per ADL-020's archive-at-parity clause; see archiveRoutes.ts).
// Help & Compliance (below) is global chrome, not a product area, and is
// unaffected by the registry.
import { getReportSection } from "@/features/report/sections";
import { navigateToHashOnCurrentPage } from "@/features/report/components/anchorNavigation";
import { PRODUCT_AREAS } from "@/features/navigation/productAreas";
import type {
  ProductArea,
  ProductAreaNavItem,
} from "@/features/navigation/types";

/**
 * One `PRODUCT_AREAS` item → one Cloudscape sidebar item. An item with its own nested `items`
 * (mission report-nav-and-narrative-01KXE9QP, WP02/T004 — today, only Report's per-section
 * items carry subsections) becomes a `link-group`: its own href still navigates to the section
 * page (clicking the header text), and its children are ALWAYS visible below it (Cloudscape's
 * `link-group`, unlike `expandable-link-group`, has no collapsed state — see research R2/decision
 * `plan.architecture.nav-mechanics`), so every subsection is reachable without an extra expand
 * click (NFR-002). An item with no nested items renders as a plain link, exactly as before.
 */
function buildNavLinkItem(
  item: ProductAreaNavItem,
  pageSections: PageSections,
): SideNavigationProps.Item {
  /* A page that carries its own in-page sections contributes them HERE, to its own nav entry,
     rather than the registry declaring them statically. Only the dashboard does this today, and
     it must: which of its measures exist is a fact about the payload, not about the product, so
     a static list would offer anchors that are not on the page. See `DashboardContentsContext`.

     Registry-declared children (Report's subsections) and published ones are the same shape and
     render identically; a page never has both. */
  const published =
    pageSections && pageSections.href === item.href ? pageSections.items : null;
  const children = published ?? item.items ?? [];
  if (children.length > 0) {
    return {
      type: "link-group",
      text: item.label,
      href: item.href,
      items: children.map(
        (child): SideNavigationProps.Link => ({
          type: "link",
          text: child.label,
          href: child.href,
        }),
      ),
    };
  }
  return { type: "link", text: item.label, href: item.href };
}

/**
 * One `PRODUCT_AREAS` entry → one Cloudscape `expandable-link-group` (mission 016 post-merge
 * sidebar-IA fix, Neil's "all collapsible groups"): EVERY product area renders identically — a
 * foldable group with the same chevron, whose header text is the area label and whose header link
 * is the area's landing (`area.items[0]?.href`). No area is special-cased. This supersedes the
 * old split where only `report` was an `expandable-link-group` and every other area was a plain,
 * always-open `section` (no chevron, no fold) — which made Report look special.
 *
 * Header-duplicate elision (one rule, applied to every area): a shallow area's first item often
 * repeats the area name AND its landing href (e.g. a "Benchmarking" area led by a "Benchmarking"
 * → /benchmark item, or "Configuration" → /admin/config). Rendering both the group header and
 * that first child would read as a duplicated line. Since the header IS that entry, the redundant
 * first child is dropped, leaving the remaining distinct items as children (Benchmarking → child
 * "Explore"; Configuration → children Facility register / Vintage worklist / …). An area whose
 * first item DIFFERS from the header is kept verbatim — Waste Capacity Study keeps its distinct
 * "Contents" (/report) child plus every nested section→subsection `link-group` (via
 * `buildNavLinkItem`, depth preserved); Admin keeps its "Tenants" child. `defaultExpanded: true`
 * gives every area the same open-on-first-paint impression, uniformly.
 */
/**
 * Sections the CURRENTLY OPEN page has published for its own nav entry, or null when no page
 * has (which is every page but the dashboard). See `buildNavLinkItem`.
 */
type PageSections = { href: string; items: ProductAreaNavItem[] } | null;

function buildProductAreaNavItem(
  area: ProductArea,
  pageSections: PageSections,
): SideNavigationProps.Item {
  const landingHref = area.items[0]?.href ?? "#";
  const firstDuplicatesHeader =
    area.items[0]?.label === area.label && area.items[0]?.href === landingHref;
  const children = firstDuplicatesHeader ? area.items.slice(1) : area.items;
  return {
    type: "expandable-link-group",
    text: area.label,
    href: landingHref,
    defaultExpanded: true,
    items: children.map((item) => buildNavLinkItem(item, pageSections)),
  };
}

function buildNavItems(
  role: string | undefined,
  pageSections: PageSections,
): SideNavigationProps.Item[] {
  // Single gating declaration per area (FR-011): `visibility: "all"` always
  // renders; `visibility: "super_admin"` renders only for that role. Unlike
  // the retired london-pack gating, this does NOT consult view-as/tenant
  // impersonation — Configuration/Admin have only ever gated on the
  // session's own role (pre-existing mission-011 behaviour, preserved).
  const visibleAreas = PRODUCT_AREAS.filter(
    (area) => area.visibility === "all" || role === "super_admin",
  );
  const items: SideNavigationProps.Item[] = [];
  // Uniform divider after every product-area group (no report-specific special case) — each
  // collapsible area is visually separated from the next and from the Help & Compliance chrome
  // that follows. The last area's trailing divider doubles as the separator before Help &
  // Compliance, so no extra standalone divider is pushed here.
  visibleAreas.forEach((area) => {
    items.push(buildProductAreaNavItem(area, pageSections));
    items.push({ type: "divider" });
  });

  items.push({
    type: "section",
    text: "Help & Compliance",
    items: [
      { type: "link", text: "Help", href: "/help" },
      {
        type: "link",
        text: "Methodology",
        href: "/help/methodology/arisings-projections",
      },
      { type: "link", text: "Accessibility", href: "/accessibility" },
      { type: "link", text: "Terms of Use", href: "/terms" },
      { type: "link", text: "Privacy Policy", href: "/privacy" },
    ],
  });

  return items;
}

// ---------------------------------------------------------------------------
// Breadcrumbs — derived from pathname
// ---------------------------------------------------------------------------

const BREADCRUMB_MAP: Record<string, string> = {
  "/admin": "Admin",
  "/admin/tenants": "Tenants",
  "/admin/registry": "Registry Explorer",
  "/admin/config": "Configuration",
  // Out-of-map wiring edit (mission config-usability-01KXKPR7/WP02, declared per that WP's own
  // brief) — the facility register list; the per-permit detail route (`/admin/config/register/
  // {permit}`) has no fixed entry here (a dynamic segment) and instead falls through to the
  // generic `/admin` breadcrumb branch below, whose "Detail" placeholder
  // `DecisionDetail.tsx`'s own `useBreadcrumbOverride().setLeafText` call replaces once the
  // facility's name (or permit) is known — the same pattern `drafts/[id]/page.tsx` uses.
  "/admin/config/register": "Facility register",
  "/help": "Help",
  "/settings": "Settings",
  "/accessibility": "Accessibility",
  "/privacy": "Privacy Policy",
  "/terms": "Terms of Use",
  "/about": "About",
  "/data-access": "Data Access",
};

/** "capacity-gap" → "Capacity Gap" — used for the archive breadcrumb leaf,
 * which has no per-page title registry (ADL-020: no further investment in
 * archived-page chrome beyond the banner + move). */
function humanizeSlug(slug: string): string {
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function buildBreadcrumbs(pathname: string): { text: string; href: string }[] {
  const crumbs: { text: string; href: string }[] = [];

  if (pathname.startsWith("/archive")) {
    // "Archive" itself isn't a real page (ADL-020 archived the whole
    // /dashboard tree, not to a new index) — its crumb points at the report
    // contents page, the actual replacement surface.
    crumbs.push({ text: "Archive", href: "/report" });
    if (pathname !== "/archive") {
      const slug = pathname.slice("/archive/".length).split("/")[0];
      crumbs.push({ text: humanizeSlug(slug), href: pathname });
    }
  } else if (pathname.startsWith("/report")) {
    // Section title comes from the same REPORT_SECTIONS registry that drives the sidebar
    // (via PRODUCT_AREAS' report items) and the section route itself — one source of
    // truth, not a second hardcoded title list.
    crumbs.push({ text: "Report", href: "/report" });
    if (pathname !== "/report") {
      const slug = pathname.slice("/report/".length).split("/")[0];
      const section = getReportSection(slug);
      crumbs.push({
        text: section?.title ?? pathname.split("/").pop() ?? "",
        href: pathname,
      });
    }
  } else if (BREADCRUMB_MAP[pathname]) {
    crumbs.push({ text: BREADCRUMB_MAP[pathname], href: pathname });
  } else if (pathname.startsWith("/admin")) {
    crumbs.push({ text: "Admin", href: "/admin/tenants" });
    const base = pathname.match(/^\/admin\/[^/]+/)?.[0];
    if (base && BREADCRUMB_MAP[base]) {
      crumbs.push({ text: BREADCRUMB_MAP[base], href: base });
    }
    // Source detail: /admin/registry/sources/{sourceId}
    const sourceMatch = pathname.match(/^\/admin\/registry\/sources\/([^/]+)$/);
    if (sourceMatch) {
      crumbs.push({
        text: decodeURIComponent(sourceMatch[1]),
        href: pathname,
      });
    } else {
      const parts = pathname.split("/");
      if (parts.length > 3) {
        crumbs.push({ text: "Detail", href: pathname });
      }
    }
  }

  return crumbs;
}

// ---------------------------------------------------------------------------
// Config drawer (super_admin only) — the way into the methodology configuration
// workspace.
//
// This reserved a slot and described the configuration epic in the future tense
// ("the configuration epic builds it out"). That epic has since shipped: the
// draft lifecycle, facility register, worklist, pending queue and handbook are
// all live under /admin/config. So the panel was promising work that already
// existed while giving no way to reach it — a dead end at the only entry point
// a super-admin would think to try.
// ---------------------------------------------------------------------------

// Deliberately NOT a link list. The sidebar's own Configuration area already carries all five
// destinations (`features/navigation/productAreas.ts`) for exactly this role, so repeating them
// here would be a second navigation for one set of screens — the duplication problem this
// config-usability work exists to remove, not add to. What the sidebar cannot say is what the
// configuration currently IS, so that is what this panel says, with one way to act on it.
function ConfigDrawer(): React.JSX.Element {
  const router = useRouter();
  return (
    <SpaceBetween size="s">
      <Box variant="h3" padding={{ top: "s" }}>
        Methodology configuration
      </Box>
      <Box variant="p" color="text-body-secondary">
        Every published figure comes from one governed configuration: the
        filters that decide which records count, and the curated decisions
        recorded against individual facilities. Nothing outside it changes a
        number.
      </Box>
      <Box variant="p" color="text-body-secondary">
        To see the rules behind the figures on a report page, use{" "}
        <b>View configuration rules</b> at the top of that page. To change them,
        work in the configuration workspace — edits go to a draft, and no
        published figure moves until that draft is materialised and activated.
      </Box>
      <Button
        variant="primary"
        onClick={() => router.push("/admin/config")}
        data-testid="config-drawer-workspace"
      >
        Open configuration workspace
      </Button>
      <Box variant="small" color="text-body-secondary">
        The facility register, vintage worklist, pending decisions and handbook
        are in the Configuration section of the sidebar.
      </Box>
    </SpaceBetween>
  );
}

// ---------------------------------------------------------------------------
// AppShell
// ---------------------------------------------------------------------------

export function AppShell({ children }: AppShellProps): React.JSX.Element {
  const router = useRouter();
  const pathname = usePathname();
  const { user } = useAuth();
  const { effectiveRole } = useClientView();
  const { isOpen: helpOpen, setOpen: setHelpOpen } = useHelpPanel();
  const {
    isOpen: inspectOpen,
    open: openInspect,
    close: closeInspect,
  } = useInspect();
  const explainPanel = useExplainPanel();
  const { leafText } = useBreadcrumbOverride();
  const dashboardContents = useDashboardContents();
  /* Hiding an area from the sidebar does not stop a reader being ON it — a back-button, a
     bookmark or a tab open before the view switched all land on the full surface. See
     `useClientViewRouteGuard`; it reads the same registry the sidebar does. */
  useClientViewRouteGuard();

  // One TanStack Query client per AppShell mount (mission drilldown-substrate, WP05/T017/T018)
  // — `useExplain` (used by `ExplainPanel`, mounted unconditionally below in the `splitPanel`
  // slot regardless of `splitPanelOpen`) needs a `QueryClientProvider` ancestor. Created here
  // (the recommended `useState` lazy-init pattern — never at module scope, which would leak a
  // single client across every AppShell instance/request) rather than in the app's root layout:
  // this is the only consumer today, so scoping it to AppShell keeps the dependency contained
  // to this WP's owned files.
  const [queryClient] = useState(() => new QueryClient());

  // Explanation SplitPanel (mission drilldown-substrate, WP05/T018, R5; scoping + toggle fixed
  // WP05 cycle-2, Issue 2) — a SEPARATE AppLayout slot from the `drawers` rail above (help/
  // inspect/config): Cloudscape's `splitPanel` and `drawers` are independent regions, so the
  // explanation panel and (e.g.) the methodology help drawer can be open AT THE SAME TIME
  // (verified: `__tests__/explain/coexistence.test.tsx`). Content is driven entirely by
  // `ExplainPanelContext` (only report pages' clickable figures ever call `open()` — see that
  // context's docstring), so this component never needs to know WHAT figure is open, only
  // whether to show the panel; closed by default (`isOpen` starts `false`).
  //
  // `explainPanel.request` gates whether the `splitPanel` prop is supplied AT ALL (below): before
  // any figure has ever been clicked in this session, and on every page that never renders a
  // clickable figure, there is nothing to show, so the slot — including its own collapsed "Open
  // explanation panel" toggle bar — is entirely absent, not just empty. Cycle-1 review found that
  // bar rendering (and doing nothing when clicked) on EVERY page, including non-report ones.
  //
  // `appLayoutRef.focusSplitPanel()` on the OPEN transition satisfies "open moves focus to panel
  // header" (NFR-003) via Cloudscape's own built-in split-panel focus management, rather than a
  // bespoke DOM ref; focus-RETURN to the invoking cell is `ExplainPanelContext.close()`'s own job
  // (it stores a `restoreFocus` callback captured at `open()` time).
  const appLayoutRef = useRef<AppLayoutProps.Ref>(null);
  const wasExplainOpenRef = useRef(false);
  useEffect(() => {
    if (explainPanel.isOpen && !wasExplainOpenRef.current) {
      appLayoutRef.current?.focusSplitPanel();
    }
    wasExplainOpenRef.current = explainPanel.isOpen;
  }, [explainPanel.isOpen]);

  // Clear a stale explanation on a genuine page navigation (WP05 cycle-2, Issue 2
  // follow-through) — `ExplainPanelProvider` is mounted once, above every route, so without
  // this a figure explained on one report page keeps showing, unchanged, after a client-side
  // navigation to a DIFFERENT page (live-verified: Table 13's explanation from `/report/capacity`
  // was still open on `/report/arisings` after following a sidebar link — Next's client-side
  // routing never unmounts this component between routes). Keyed on `pathname` alone (never the
  // hash — a same-page subsection jump doesn't change it, see `subscribeToHashChange`'s comment
  // above), and guarded by a ref so it never fires on first mount (nothing to clear then).
  const previousPathnameRef = useRef(pathname);
  useEffect(() => {
    if (previousPathnameRef.current !== pathname) {
      previousPathnameRef.current = pathname;
      explainPanel.reset();
    }
  }, [pathname, explainPanel]);

  // Hash-aware active state for the sidebar's subsection links — see `subscribeToHashChange`'s
  // docstring above for why this is a `useSyncExternalStore`, not `useState`/`useEffect`.
  const hash = useSyncExternalStore(
    subscribeToHashChange,
    getHashSnapshot,
    getServerHashSnapshot,
  );

  // Bridge Help + Inspect + Config into the AppLayout multi-drawer API (US2/US3). Only one drawer
  // shows at a time; opening one closes the others. Help/Inspect are driven by their contexts (a
  // page's openWithContent, a figure's open(ref)); Config is a reserved placeholder slot (FR-012)
  // so the future config-panel epic needs no rework — its state is local here.
  const [configOpen, setConfigOpen] = useState(false);
  const activeDrawerId = inspectOpen
    ? "inspect"
    : helpOpen
      ? "help"
      : configOpen
        ? "config"
        : null;
  const onActiveDrawerChange = useCallback(
    (id: string | null) => {
      setHelpOpen(id === "help");
      if (id === "inspect") openInspect();
      else closeInspect();
      setConfigOpen(id === "config");
    },
    [setHelpOpen, openInspect, closeInspect],
  );

  // Reserved Config slot (FR-012, super_admin only) — typed against Cloudscape's Drawer so the
  // literal trigger icon isn't widened to string when spread into the drawers array.
  const configDrawers: AppLayoutProps.Drawer[] =
    user?.role === "super_admin"
      ? [
          {
            id: "config",
            ariaLabels: {
              drawerName: "Methodology configuration",
              triggerButton: "Open methodology configuration",
              closeButton: "Close methodology configuration",
              resizeHandle: "Resize methodology configuration",
            },
            trigger: { iconName: "settings" },
            content: <ConfigDrawer />,
            resizable: true,
            defaultSize: 360,
          },
        ]
      : [];

  // PRODUCT_AREAS visibility gates on the session's own role only (FR-011) —
  // unlike the retired london-pack dashboard gating, it does not consult
  // view-as/tenant impersonation (see buildNavItems' comment).
  // Built from the EFFECTIVE role, so "view as" changes the sidebar and not merely the data.
  //
  // The dashboard's own sections are nested under its nav entry, from what the page PUBLISHED
  // rather than from the registry — they are the page's contents, and only the page knows which
  // of them this environment and this reader actually got (FR-016/FR-017). This is what used to
  // be the horizontal contents strip at the top of the dashboard; it reads better as a menu, and
  // it stops the first screen being spent on a list of what is below it.
  const dashboardEntries = dashboardContents.entries;
  const pageSections = useMemo<PageSections>(
    () =>
      dashboardEntries.length > 0
        ? {
            href: "/household",
            items: dashboardEntries.map((entry) => ({
              label: entry.title,
              href: `/household#${entry.anchorId}`,
            })),
          }
        : null,
    [dashboardEntries],
  );
  const navItems = useMemo(
    () => buildNavItems(effectiveRole, pageSections),
    [effectiveRole, pageSections],
  );
  // Group items (a section's own link-group header) match on pathname alone, the way they
  // always have; a subsection child link's href includes the hash, so it only matches —
  // and only highlights — once that hash is the current one (R2: "pathname match for the
  // group, hash-aware highlight for the child").
  const activeHref = hash ? `${pathname}${hash}` : pathname;
  const breadcrumbs = useMemo(() => {
    const crumbs = buildBreadcrumbs(pathname);
    if (leafText && crumbs.length > 0) {
      crumbs[crumbs.length - 1] = {
        ...crumbs[crumbs.length - 1],
        text: leafText,
      };
    }
    return crumbs;
  }, [pathname, leafText]);

  const handleSideNavFollow = useCallback(
    (event: CustomEvent<SideNavigationProps.FollowDetail>) => {
      event.preventDefault();
      const href = event.detail.href;
      if (!href) return;

      const hashIndex = href.indexOf("#");
      const path = hashIndex === -1 ? href : href.slice(0, hashIndex);
      const targetHash = hashIndex === -1 ? "" : href.slice(hashIndex);

      if (path === pathname) {
        // Same-page navigation — the target page is already mounted, so no route change (the
        // mission-012 follow-handler pattern, reused here for the sidebar: R2/NFR-003). A
        // subsection hash scrolls+focuses in place; a bare re-click of the section's own header
        // link (no hash) just clears any hash currently deep-linked into this page.
        if (targetHash) {
          navigateToHashOnCurrentPage(targetHash.slice(1));
        } else if (window.location.hash) {
          window.history.replaceState(null, "", pathname);
          // `replaceState` doesn't fire `hashchange` (neither does `pushState`) — dispatch a
          // synthetic one so the `useSyncExternalStore` subscription above re-checks and picks
          // up the now-empty hash (clearing the sidebar's hash-aware highlight).
          window.dispatchEvent(new Event("hashchange"));
        }
        return;
      }
      router.push(href);
    },
    [router, pathname],
  );

  const handleSignOut = useCallback(() => {
    document.cookie =
      "access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    router.push("/login");
  }, [router]);

  const handleSettings = useCallback(() => {
    router.push("/settings");
  }, [router]);

  useEffect(() => {
    const saved = localStorage.getItem("rf-density");
    applyDensity(saved === "compact" ? Density.Compact : Density.Comfortable);
  }, []);

  // Super-admin "view as" tenant list — moved out of the standalone TenantSwitcher into a
  // TopNavigation menu-dropdown utility. Only fetched when a super_admin is NOT already viewing
  // as a tenant. getViewAsTenantId reads a cookie; safe here — this subtree is client-only (the
  // (auth) layout renders null until authenticated, so AppShell never server-renders).
  /* `isSuperAdmin` here is about ENTITLEMENT — may this reader use the view-as switcher and
     the admin fetches below. It stays keyed on the reader's own role, because impersonating a
     tenant does not stop you being an admin.

     The CHROME is a different question and uses `effectiveRole` (see `useClientView`): a
     super_admin viewing as a tenant should see that tenant's sidebar, which is the entire
     point of the feature and was not true until 2026-09-24. */
  const isSuperAdmin = user?.role === "super_admin";
  /* Where the wordmark goes. RF internal keeps the study as its front door — that is the
     surface they work in; a planner gets the dashboard, which is the only area their sidebar
     offers. Derived from the same role the nav is built from, so the two cannot disagree. */
  const homeHref = effectiveRole === "super_admin" ? "/report" : "/household";
  const isViewingAs = !!getViewAsTenantId();
  const [viewAsTenants, setViewAsTenants] = useState<
    { id: string; name: string }[]
  >([]);
  useEffect(() => {
    if (!isSuperAdmin || isViewingAs) return;
    let cancelled = false;
    fetch("/api/admin/tenants")
      .then((res) => res.json())
      .then((json) => {
        if (cancelled) return;
        const list = json.data?.tenants as
          | { id: string; name: string; active: boolean }[]
          | undefined;
        if (list) {
          setViewAsTenants(
            list
              .filter((t) => t.active)
              .map((t) => ({ id: t.id, name: t.name })),
          );
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, isViewingAs]);

  const switchToTenant = useCallback(async (tenantId: string) => {
    try {
      const res = await fetch("/api/admin/tenants/switch-tenant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant_id: tenantId }),
      });
      const json = await res.json();
      if (!res.ok) return;
      setViewAsTenant(json.data.tenant_id, json.data.tenant_name);
    } catch {
      // Switching failed — leave the menu as-is; the admin can retry.
    }
  }, []);

  const userDescription =
    [user?.tenantName, user?.role].filter(Boolean).join(" · ") || undefined;

  const topNavUtilities: TopNavigationProps.Utility[] = [
    {
      type: "button",
      iconName: "status-info",
      text: "Help",
      ariaLabel: "Help",
      onClick: () => router.push("/help"),
    },
    ...(isSuperAdmin && !isViewingAs && viewAsTenants.length > 0
      ? ([
          {
            type: "menu-dropdown",
            iconName: "share",
            text: "View as",
            ariaLabel: "Switch tenant view",
            items: viewAsTenants.map((t) => ({ id: t.id, text: t.name })),
            onItemClick: ({ detail }) => switchToTenant(detail.id),
          },
        ] satisfies TopNavigationProps.Utility[])
      : []),
    {
      type: "menu-dropdown",
      iconName: "user-profile",
      text: user?.email ?? "User",
      description: userDescription,
      ariaLabel: "User menu",
      items: [
        { id: "settings", text: "Settings" },
        { id: "signout", text: "Sign out" },
      ],
      onItemClick: ({ detail }) => {
        if (detail.id === "settings") handleSettings();
        else if (detail.id === "signout") handleSignOut();
      },
    },
  ];

  return (
    <QueryClientProvider client={queryClient}>
      <div style={{ minWidth: 1280 }}>
        {/* Cloudscape TopNavigation, branded Dark Violet via the "top-navigation" visual
            context in forwardTheme. `id="h"` is kept so AppLayout's headerSelector still
            offsets the layout by the header height. */}
        <div id="h">
          <TopNavigation
            identity={{
              // The product's front door, which is not the same page for every reader: the
              // study is RF-internal for the GLA soft launch (call notes, 2026-09-23), so a
              // planner clicking the wordmark would have landed on an area their sidebar
              // deliberately does not offer. The dashboard IS the front door — see
              // `navigation/productAreas.ts`, whose first area is `household`.
              href: homeHref,
              title: PRODUCT_NAME,
              // Temporary Forward mark (an Iris badge with a forward chevron) pending the final
              // brand asset — see public/forward-mark.svg / docs/design/forward-brand-system.md.
              logo: { src: "/forward-mark.svg", alt: PRODUCT_NAME },
              onFollow: (e) => {
                e.preventDefault();
                router.push(homeHref);
              },
            }}
            utilities={topNavUtilities}
            i18nStrings={{
              overflowMenuTriggerText: "More",
              overflowMenuTitleText: "All",
              searchIconAriaLabel: "Search",
              searchDismissIconAriaLabel: "Close search",
            }}
          />
        </div>

        <ViewingAsBanner />

        <AppLayout
          ref={appLayoutRef}
          activeDrawerId={activeDrawerId}
          onDrawerChange={({ detail }) =>
            onActiveDrawerChange(detail.activeDrawerId)
          }
          splitPanelOpen={explainPanel.isOpen}
          onSplitPanelToggle={({ detail }) => {
            // A fresh OPEN always goes through a clickable figure's `ExplainPanelContext.open()`
            // (T019), which sets its own `request`. This handler only ever fires for the
            // SplitPanel's OWN controls: the collapsed bar's "open" toggle (re-show the current
            // request — `reopen()`, never a no-op: cycle-1 review found this branch silently
            // dropped `detail.open === true`) and the expanded panel's close control/Escape
            // (`close()`, which also returns focus to the invoking cell per NFR-003).
            if (detail.open) {
              explainPanel.reopen();
            } else {
              explainPanel.close();
            }
          }}
          splitPanel={
            explainPanel.request ? (
              <SplitPanel
                header="Explanation"
                i18nStrings={{
                  closeButtonAriaLabel: "Close explanation panel",
                  openButtonAriaLabel: "Open explanation panel",
                  resizeHandleAriaLabel: "Resize explanation panel",
                  preferencesTitle: "Explanation panel preferences",
                  preferencesPositionLabel: "Panel position",
                  preferencesPositionDescription:
                    "Choose the position of the explanation panel.",
                  preferencesPositionSide: "Side",
                  preferencesPositionBottom: "Bottom",
                  preferencesConfirm: "Confirm",
                  preferencesCancel: "Cancel",
                }}
              >
                <ExplainPanel />
              </SplitPanel>
            ) : undefined
          }
          // Out-of-map point fix (WP07/T023, axe browser pass): AppLayout's own navigation
          // drawer controls (the mobile close button in particular) ship with NO accessible
          // name unless `ariaLabels` is supplied at this top level — found live via axe on
          // every page (button-name, critical). The two `drawers` entries below already label
          // their own controls this thoroughly; this closes the one gap axe found in the
          // built-in navigation drawer, matching Cloudscape's own documented example labels.
          ariaLabels={{
            navigation: "Navigation drawer",
            navigationClose: "Close navigation drawer",
            navigationToggle: "Open navigation drawer",
          }}
          drawers={[
            {
              id: "help",
              ariaLabels: {
                drawerName: "Methodology help",
                triggerButton: "Open methodology help",
                closeButton: "Close methodology help",
                resizeHandle: "Resize methodology help",
              },
              trigger: { iconName: "status-info" },
              content: <HelpPanelWidget />,
              resizable: true,
              defaultSize: 360,
            },
            {
              id: "inspect",
              ariaLabels: {
                drawerName: "Inspect figure",
                triggerButton: "Open figure inspection",
                closeButton: "Close figure inspection",
                resizeHandle: "Resize figure inspection",
              },
              trigger: { iconName: "search" },
              content: <InspectDrawerContent />,
              resizable: true,
              defaultSize: 420,
            },
            // Reserved Config slot (FR-012, super_admin only) — the future methodology-config panel
            // lands here. Placeholder today so the drawer rail + wiring already exist.
            ...configDrawers,
          ]}
          navigationHide={false}
          navigation={
            <SideNavigation
              activeHref={activeHref}
              items={navItems}
              onFollow={handleSideNavFollow}
            />
          }
          breadcrumbs={
            breadcrumbs.length > 0 ? (
              <BreadcrumbGroup
                items={breadcrumbs}
                onFollow={(e) => {
                  e.preventDefault();
                  router.push(e.detail.href);
                }}
              />
            ) : undefined
          }
          content={
            <div>
              {children}
              <Footer />
            </div>
          }
          headerSelector="#h"
          disableContentPaddings={false}
        />
      </div>
    </QueryClientProvider>
  );
}
